import { useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { useNavigate, useParams } from "react-router-dom";
import Form from "./Form";
import {
  getOnePackage,
  updatePackage,
} from "../../../actions/management.action";

const EditPackages = () => {
  const [data, setData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // Validation helper
  const validatePackageData = () => {
    const errors = [];
    const titleRegex = /^[a-zA-Z0-9\s,.'-]+$/;

    // Required string fields
    if (!data.packageTitle || !data.packageTitle.trim()) {
      errors.push("Package Title is required");
    } else if (!titleRegex.test(data.packageTitle.trim())) {
      errors.push(
        "Package Title can only include letters, numbers, spaces, commas, periods, apostrophes, and dashes",
      );
    }
    if (!data.uniqueCode || !data.uniqueCode.toString().trim()) {
      errors.push("Unique Code is required");
    }
    if (!data.startLocation || !data.startLocation.trim()) {
      errors.push("Start Location is required");
    }
    if (!data.endLocation || !data.endLocation.trim()) {
      errors.push("End Location is required");
    }
    if (!data.category || !data.category.trim()) {
      errors.push("Category is required");
    }
    if (!data.duration || !data.duration.trim()) {
      errors.push("Duration is required");
    }
    if (!data.packageType || !data.packageType.trim()) {
      errors.push("Package Type is required");
    }
    if (!data.pricingType || !data.pricingType.toString().trim()) {
      errors.push("Pricing Type is required");
    }
    if (data.amountWithoutStay === undefined || data.amountWithoutStay === "") {
      errors.push("Amount Without Stay is required");
    }
    if (data.amountWithStay === undefined || data.amountWithStay === "") {
      errors.push("Amount With Stay is required");
    }
    if (
      data.pricingType === "perPerson" &&
      (!data.minTravellers || Number(data.minTravellers) <= 0)
    ) {
      errors.push("Minimum Travellers must be greater than 0");
    }

    // Numeric fields
    if (!data.travellers || data.travellers <= 0) {
      errors.push("Number of Travellers must be greater than 0");
    }

    return errors;
  };

  const handleSubmit = async (payload) => {
    // Validate before submitting
    const validationErrors = validatePackageData();
    if (validationErrors.length > 0) {
      toast({
        title: "Validation Error",
        description: validationErrors.join("\n"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      payload.append("id", id);
      const response = await updatePackage(payload);

      if (response?.success) {
        toast({
          title: "Success",
          description: "Package updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Navigate to package detail page after toast is visible
        setTimeout(() => {
          navigate(`/management/packages/${id}`);
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating package:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to update the package. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!id) {
      toast({
        title: "Error",
        description: "Package ID is missing.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/management/packages");
      return;
    }

    const fetchPackage = async () => {
      try {
        console.log("[EditPackage] Fetching package with ID:", id);
        const response = await getOnePackage(id);
        console.log("[EditPackage] Response received:", response);

        if (response?.success && response?.data) {
          // Parse journey if it's a string
          let journeyData = [];
          try {
            if (typeof response.data.journey === "string") {
              journeyData = JSON.parse(response.data.journey);
            } else if (Array.isArray(response.data.journey)) {
              journeyData = response.data.journey;
            }
          } catch (parseError) {
            console.warn("[EditPackage] Journey parsing failed:", parseError);
            journeyData = [];
          }

          // Parse stops if it's a string
          let stopsData = [];
          try {
            if (typeof response.data.stops === "string") {
              stopsData = JSON.parse(response.data.stops);
            } else if (Array.isArray(response.data.stops)) {
              stopsData = response.data.stops;
            }
          } catch (parseError) {
            console.warn("[EditPackage] Stops parsing failed:", parseError);
            stopsData = [];
          }

          setData({
            ...response.data,
            journey: journeyData,
            stops: stopsData,
          });
          console.log("[EditPackage] Data set successfully");
        } else {
          throw new Error(response?.message || "Invalid response format");
        }
      } catch (error) {
        console.error("[EditPackage] Error fetching package data:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to fetch package details.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        // Don't navigate away - let user see the error and try again
      }
    };

    fetchPackage();
  }, [id]); // Only depend on id, not navigate and toast

  return (
    <Box p={8} minH="100vh">
      <BreadCrumbMaker
        items={[
          // { link: "/", title: "Dashboard" },
          { link: "/management", title: "Operations Setup" },
          { link: "/management/packages", title: "Packages" },
          {
            link: `/management/packages/edit/${id}`,
            title: "Edit Package",
            on: true,
          },
        ]}
      />

      <Box bg="white" p={8} borderRadius="md" boxShadow="sm">
        <Form
          data={data}
          setData={setData}
          handleSubmit={handleSubmit}
          formRef={formRef}
          isEditMode={true}
          isSaving={isSaving}
        />
      </Box>
    </Box>
  );
};

export default EditPackages;
